import { localApiClient } from "./localApiClient";

const norm = (value) => String(value || "").trim();

export const fromLocalClinic = (clinic = {}) => ({
  ...clinic,
  id: clinic.id,
  hospitalId: clinic.hospitalId,
  name: clinic.name || "",
  departmentName: clinic.departmentName || "",
  location: clinic.location || "",
  workingDays: Array.isArray(clinic.workingDays) ? clinic.workingDays : [],
  startTime: clinic.startTime || "",
  endTime: clinic.endTime || "",
  status: clinic.status === "INACTIVE" ? "Inactive" : "Active",
});

const toLocalClinicPayload = (hospitalId, clinicData = {}) => ({
  hospitalId,
  name: norm(clinicData.name || clinicData.clinicName),
  departmentName: norm(clinicData.departmentName || clinicData.department) || undefined,
  location: norm(clinicData.location) || undefined,
  workingDays: Array.isArray(clinicData.workingDays) ? clinicData.workingDays : undefined,
  startTime: clinicData.startTime || undefined,
  endTime: clinicData.endTime || undefined,
});

const toLocalClinicPatch = (clinicData = {}) => {
  const patch = {};
  if (clinicData.name !== undefined || clinicData.clinicName !== undefined) patch.name = norm(clinicData.name || clinicData.clinicName);
  if (clinicData.departmentName !== undefined || clinicData.department !== undefined) {
    patch.departmentName = norm(clinicData.departmentName || clinicData.department) || undefined;
  }
  if (clinicData.location !== undefined) patch.location = norm(clinicData.location) || undefined;
  if (clinicData.workingDays !== undefined) patch.workingDays = Array.isArray(clinicData.workingDays) ? clinicData.workingDays : [];
  if (clinicData.startTime !== undefined) patch.startTime = clinicData.startTime || undefined;
  if (clinicData.endTime !== undefined) patch.endTime = clinicData.endTime || undefined;
  if (clinicData.status !== undefined) {
    const status = norm(clinicData.status).toLowerCase();
    patch.status = status === "inactive" || status === "disabled" ? "INACTIVE" : "ACTIVE";
  }
  return patch;
};

const wrapLocalError = (action, error) => {
  const message = error?.message || String(error);
  if (/bearer|jwt|unauthor/i.test(message)) {
    throw new Error(`${action} failed: Local API token is missing or expired.`);
  }
  if (/hospital|scope|forbidden/i.test(message)) {
    throw new Error(`${action} failed: Hospital scope mismatch.`);
  }
  if (/failed to fetch|network/i.test(message)) {
    throw new Error(`${action} failed: Local API is unavailable.`);
  }
  throw new Error(`${action} failed: ${message}`);
};

export const localClinicService = {
  async fetchClinics(hospitalId) {
    try {
      if (!hospitalId) throw new Error("Hospital ID is required.");
      const rows = await localApiClient.get(`/clinics/${encodeURIComponent(hospitalId)}`);
      return (Array.isArray(rows) ? rows : []).map(fromLocalClinic);
    } catch (error) {
      wrapLocalError("Fetch clinics", error);
    }
  },

  async fetchPaginatedClinics(hospitalId, pageSize = 20, lastDoc = null, filters = {}) {
    const q = norm(filters.name || filters.location).toLowerCase();
    let clinics = await this.fetchClinics(hospitalId);
    if (q) {
      clinics = clinics.filter((clinic) =>
        [clinic.name, clinic.location, clinic.departmentName].some((value) => norm(value).toLowerCase().includes(q))
      );
    }
    clinics = clinics.slice(0, pageSize);
    return {
      clinics,
      lastVisible: null,
      lastDoc: null,
      hasMore: clinics.length === pageSize,
    };
  },

  async addClinic(hospitalId, clinicData) {
    try {
      if (!hospitalId) throw new Error("Hospital ID is required.");
      const clinic = await localApiClient.post("/clinics", toLocalClinicPayload(hospitalId, clinicData));
      return { success: true, message: "Clinic added successfully.", id: clinic.id, data: fromLocalClinic(clinic) };
    } catch (error) {
      wrapLocalError("Create clinic", error);
    }
  },

  async updateClinic(hospitalId, clinicId, clinicData) {
    try {
      if (!hospitalId || !clinicId) throw new Error("Hospital ID and clinic ID are required.");
      const clinic = await localApiClient.patch(
        `/clinics/${encodeURIComponent(hospitalId)}/${encodeURIComponent(clinicId)}`,
        toLocalClinicPatch(clinicData)
      );
      return { success: true, message: "Clinic updated successfully.", data: fromLocalClinic(clinic) };
    } catch (error) {
      wrapLocalError("Update clinic", error);
    }
  },

  async deleteClinic(hospitalId, clinicId) {
    try {
      if (!hospitalId || !clinicId) throw new Error("Hospital ID and clinic ID are required.");
      const clinic = await localApiClient.delete(`/clinics/${encodeURIComponent(hospitalId)}/${encodeURIComponent(clinicId)}`);
      return { success: true, message: "Clinic disabled successfully.", data: fromLocalClinic(clinic) };
    } catch (error) {
      wrapLocalError("Disable clinic", error);
    }
  },
};

export default localClinicService;
